/**
 * Dados da aba Outros → Contatos — C-75 (ajuste de tela, 14/10) + GUPM-202.
 *
 * A aba lista TODOS os contatos: o bloco de emergência (quem tem a marcação) e o
 * diretório por empresa (quem tem empresa), no formato único `ContatoGestao`. Um
 * contato de emergência com empresa aparece nos dois.
 *  - admin: uma consulta só, com ativos E inativos (`listParaGestao`) — o inativo
 *    precisa aparecer para ser reativado;
 *  - operador e RT: as leituras de sempre, só ativos (`listAtivos` e
 *    `listSuporteTecnico`). A consulta com inativos nem sai do navegador.
 * Gate espelhado na UI; o real é a RLS de `contacts`.
 */
import type { ContatoGestao } from '@/services/datasources';
import { useContatosEmergencia } from './useContatosEmergencia';
import { useSuporteTecnico } from './useSuporteTecnico';
import { useContatosGestao } from './useContatosAdmin';

export interface BlocoContatos {
  contatos: ContatoGestao[];
  isLoading: boolean;
  isError: boolean;
}

export interface ContatosDaAba {
  emergencia: BlocoContatos;
  suporte: BlocoContatos;
  /** Todos os contatos, sem repetição (quem está nos dois blocos conta uma vez). */
  todos: ContatoGestao[];
}

/** Campos que a leitura de operador/RT não traz (não são exibidos para eles). */
const SO_LEITURA = { is_active: true, sort_order: 0, app_user_id: null, vinculo_ativo: false } as const;

export function useContatosDaAba(admin: boolean): ContatosDaAba {
  const gestao = useContatosGestao(admin);
  const emergencia = useContatosEmergencia(!admin);
  const suporte = useSuporteTecnico(!admin);

  if (admin) {
    const todos = gestao.data ?? [];
    const estado = { isLoading: gestao.isLoading, isError: gestao.isError };
    return {
      emergencia: {
        // Mesma ordem de `listAtivos` (por função), que o operador e o modal 🚨 veem.
        contatos: todos.filter((c) => c.is_emergencia).sort((a, b) => a.role.localeCompare(b.role, 'pt-BR')),
        ...estado,
      },
      suporte: { contatos: todos.filter((c) => c.empresa), ...estado },
      todos,
    };
  }

  const deEmergencia: ContatoGestao[] = (emergencia.data ?? []).map((c) => ({
    ...c,
    ...SO_LEITURA,
    is_emergencia: true,
    email: c.email ?? null,
    empresa: c.empresa ?? null,
  }));
  const doDiretorio: ContatoGestao[] = (suporte.data ?? []).map((c) => ({ ...c, ...SO_LEITURA }));
  const ids = new Set(deEmergencia.map((c) => c.id));
  return {
    emergencia: { contatos: deEmergencia, isLoading: emergencia.isLoading, isError: emergencia.isError },
    suporte: { contatos: doDiretorio, isLoading: suporte.isLoading, isError: suporte.isError },
    todos: [...deEmergencia, ...doDiretorio.filter((c) => !ids.has(c.id))],
  };
}
